import { Controller, type FieldPath, type FieldValues, useFormContext } from 'react-hook-form';
import ReactSelect, { type ReactSelectProps } from '../../../Form/ReactSelect';
import type { HookInputBaseProps } from '../../types';

export interface HookReactMultiSelectProps<T extends FieldValues>
  extends Omit<ReactSelectProps<unknown, true>, 'name'>,
    HookInputBaseProps<T> {
  name: FieldPath<T>;
  componentClassName?: string;
}

export const HookReactMultiSelect = <T extends FieldValues>(props: HookReactMultiSelectProps<T>) => {
  const { name, required, validate, id, formMethods, ...rest } = props;
  const { control } = useFormContext<T>();
  const idOrName = id || name;
  return (
    <Controller
      name={name}
      rules={{ required: required && 'Required', validate }}
      control={formMethods?.control || control}
      render={({ field, fieldState: { error, isTouched } }) => (
        <>
          <ReactSelect {...rest} {...field} id={idOrName} isMulti closeMenuOnSelect={false} error={isTouched ? error?.message : ''} />
        </>
      )}
    />
  );
};

export interface HookReactSingleSelectProps<T extends FieldValues>
  extends Omit<ReactSelectProps<unknown, false>, 'name'>,
    HookInputBaseProps<T> {
  name: FieldPath<T>;
  componentClassName?: string;
}

export const HookReactSingleSelect = <T extends FieldValues>(props: HookReactSingleSelectProps<T>) => {
  const { name, required, validate, componentClassName, formMethods, ...rest } = props;
  const { control } = useFormContext<T>();

  return (
    <Controller
      name={name}
      rules={{ required: required && 'Required', validate }}
      control={formMethods?.control || control}
      render={({ field, fieldState: { error } }) => (
        <div className={componentClassName}>
          <ReactSelect {...rest} {...field} error={error?.message} />
        </div>
      )}
    />
  );
};
